import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEur, fmtDate } from "../lib/api";
import { useApp } from "../lib/store";
import { Badge } from "./ui/badge";
import { ORDER_STATUS_COLORS } from "../lib/ordersLib";

function Panel({ testid, children, empty, t }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100" data-testid={testid}>
      {children}
      {empty && <p className="p-6 text-sm text-gray-400">{t("empty_title")}</p>}
    </div>
  );
}

function useFetch(url, pick, dep) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(url).then((r) => setItems(pick ? pick(r.data) : r.data)).catch(() => setItems([]));
  }, [url, dep]);
  return items;
}

export function AccountOrders({ accountId }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const data = useFetch(`/orders?account_id=${accountId}&page_size=50`, (d) => d);
  if (!data) return <p className="text-gray-400 text-sm">{t("loading")}</p>;
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">{t("acct_orders_recent")} · {t("total")}: {data.total}</p>
      <Panel testid="account-orders-panel" empty={!data.items.length} t={t}>
        {data.items.map((o) => (
          <div key={o.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm cursor-pointer hover:bg-amber-50/40"
            onClick={() => navigate(`/orders/${o.id}`)} data-testid="account-order-row">
            <span className="font-mono font-medium">{o.order_number}</span>
            <Badge className={`${ORDER_STATUS_COLORS[o.status] || ""} hover:bg-inherit`}>{o.status}</Badge>
            <span className="text-gray-400">{o.po_number || "–"}</span>
            <span className="text-gray-500">{fmtDate(o.order_date)}</span>
            <span className="font-mono ml-auto">{fmtEur(o.total, o.currency)}</span>
          </div>
        ))}
      </Panel>
    </div>
  );
}

export function AccountContracts({ accountId, onEdit, reloadToken }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const items = useFetch(`/contracts?account_id=${accountId}`, null, reloadToken);
  if (!items) return <p className="text-gray-400 text-sm">{t("loading")}</p>;
  return (
    <Panel testid="account-contracts-panel" empty={!items.length} t={t}>
      {items.map((c) => (
        <div key={c.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm cursor-pointer hover:bg-amber-50/40"
          onClick={() => (onEdit ? onEdit(c) : navigate(`/contracts?id=${c.id}`))} data-testid="account-contract-row">
          <span className="font-mono font-medium">{c.contract_number}</span>
          <span className="flex-1 min-w-[160px] truncate">{c.name}</span>
          <Badge variant="outline">{c.status}</Badge>
          {c.price_index && <span className="text-gray-400 text-xs">{c.price_index}</span>}
          <span className="text-gray-500">{t("end_date")}: {fmtDate(c.end_date)}</span>
          <span className="font-mono">{fmtEur(c.annual_value)}</span>
        </div>
      ))}
    </Panel>
  );
}

export function AccountConditions({ accountId, onEdit, reloadToken }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const data = useFetch(`/conditions?account_id=${accountId}&page_size=100`, (d) => d.items, reloadToken);
  if (!data) return <p className="text-gray-400 text-sm">{t("loading")}</p>;
  const typeLabel = { price: t("cond_type_price"), discount: t("cond_type_discount"),
    payment_terms: t("cond_type_payment"), freight: t("cond_type_freight") };
  return (
    <Panel testid="account-conditions-panel" empty={!data.length} t={t}>
      {data.map((c) => (
        <div key={c.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm cursor-pointer hover:bg-amber-50/40"
          onClick={() => (onEdit ? onEdit(c) : navigate(`/conditions?id=${c.id}`))} data-testid="account-condition-row">
          <Badge variant="outline">{typeLabel[c.condition_type] || c.condition_type}</Badge>
          <span className="font-mono text-xs">{c.article_number || "–"}</span>
          <span className="text-gray-500 flex-1 truncate">{c.article_name || c.notes || "–"}</span>
          <span className="font-mono">
            {c.condition_type === "discount" ? `${c.discount_pct || 0}%`
              : c.condition_type === "payment_terms" ? (c.payment_terms || "–")
              : c.unit_price != null ? fmtEur(c.unit_price) : (c.value || "–")}
          </span>
          <span className="text-gray-400 text-xs">{fmtDate(c.valid_from)} – {fmtDate(c.valid_to)}</span>
        </div>
      ))}
    </Panel>
  );
}

export function AccountArticles({ accountId, onEdit, reloadToken }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const data = useFetch(`/articles?account_id=${accountId}&page_size=200`, (d) => d.items, reloadToken);
  if (!data) return <p className="text-gray-400 text-sm">{t("loading")}</p>;
  return (
    <Panel testid="account-articles-panel" empty={!data.length} t={t}>
      {data.map((a) => (
        <div key={a.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm cursor-pointer hover:bg-amber-50/40"
          onClick={() => (onEdit ? onEdit(a) : navigate(`/articles?id=${a.id}`))} data-testid="account-article-row">
          <span className="font-mono font-medium">{a.article_number}</span>
          <span className="flex-1 min-w-[160px] truncate">{a.name}</span>
          <span className="text-gray-400 text-xs">{a.product_family}</span>
          <Badge variant="outline">{a.status}</Badge>
          <span className="font-mono">{a.price_per_1000 != null ? `${fmtEur(a.price_per_1000)}/1000` : "–"}</span>
        </div>
      ))}
    </Panel>
  );
}

export function AccountSamplesArtworks({ accountId, onEdit, reloadToken }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const samples = useFetch(`/samples?account_id=${accountId}`, null, reloadToken);
  const artworks = useFetch(`/artworks?account_id=${accountId}`, null, reloadToken);
  if (!samples || !artworks) return <p className="text-gray-400 text-sm">{t("loading")}</p>;
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">{t("sample_orders")}</p>
        <Panel testid="account-samples-panel" empty={!samples.length} t={t}>
          {samples.map((s) => (
            <div key={s.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm cursor-pointer hover:bg-amber-50/40"
              onClick={() => (onEdit ? onEdit("samples", s) : navigate(`/samples?id=${s.id}`))} data-testid="account-sample-row">
              <span className="font-mono font-medium">{s.sample_number}</span>
              <span className="text-gray-500 flex-1 truncate">{s.product_type}</span>
              <Badge variant="outline">{s.status}</Badge>
              <span className="text-gray-400 text-xs">{fmtDate(s.created_at)}</span>
            </div>
          ))}
        </Panel>
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">{t("artworks")}</p>
        <Panel testid="account-artworks-panel" empty={!artworks.length} t={t}>
          {artworks.map((a) => (
            <div key={a.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm cursor-pointer hover:bg-amber-50/40"
              onClick={() => (onEdit ? onEdit("artworks", a) : navigate(`/samples?artwork_id=${a.id}`))} data-testid="account-artwork-row">
              <span className="font-mono font-medium">{a.artwork_number}</span>
              <span className="flex-1 truncate">{a.name}</span>
              <Badge variant="outline">{a.status}</Badge>
              <span className="text-gray-400 text-xs">{fmtDate(a.created_at)}</span>
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}

export function AccountActivities({ accountId }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const items = useFetch(`/activities?account_id=${accountId}&limit=100`);
  if (!items) return <p className="text-gray-400 text-sm">{t("loading")}</p>;
  return (
    <Panel testid="account-activities-panel" empty={!items.length} t={t}>
      {items.map((a) => (
        <div key={a.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm cursor-pointer hover:bg-amber-50/40"
          onClick={() => navigate(`/activities?id=${a.id}`)} data-testid="account-activity-row">
          <Badge variant="outline">{a.type}</Badge>
          <span className="flex-1 min-w-[180px] truncate font-medium">{a.subject}</span>
          <span className="text-gray-400">{a.owner_name}</span>
          <Badge className={a.status === "Done" ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : "bg-amber-50 text-amber-700 hover:bg-amber-50"}>{a.status}</Badge>
          <span className="text-gray-500 text-xs">{fmtDate(a.due_date || a.created_at)}</span>
        </div>
      ))}
    </Panel>
  );
}

export function AccountLocations({ accountId, onEdit, reloadToken }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const items = useFetch(`/locations?account_id=${accountId}`, null, reloadToken);
  if (!items) return <p className="text-gray-400 text-sm">{t("loading")}</p>;
  return (
    <Panel testid="account-locations-panel" empty={!items.length} t={t}>
      {items.map((l) => (
        <div key={l.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm cursor-pointer hover:bg-amber-50/40"
          onClick={() => (onEdit ? onEdit(l) : navigate(`/locations?id=${l.id}`))} data-testid="account-location-row">
          <span className="font-medium min-w-[160px]">{l.name}</span>
          <Badge variant="outline">{l.site_type}</Badge>
          <span className="text-gray-500">{l.city}{l.country ? `, ${l.country}` : ""}</span>
          <span className="text-gray-400 text-xs ml-auto font-mono">{l.erp_customer_number || ""}</span>
        </div>
      ))}
    </Panel>
  );
}
